import { useEffect, useState } from 'react';
import { collection, query, where, orderBy, onSnapshot, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import DepartureTopStrip from '../../components/ui/DepartureTopStrip';

const FLAG_SEVERITY = {
  DUP_PHONE_24H: 'high',
  DUP_PHONE_NETWORK: 'medium',
  GEO_OUTSIDE_ZONE: 'high',
  GEO_JUMP: 'high',
  PHOTO_DUP_HASH: 'high',
  PHOTO_EXIF_MISMATCH: 'medium',
  OTP_RUSH: 'medium',
  RATING_PATTERN: 'low',
  OFF_SHIFT: 'low',
};

const flagChip = (flag) => {
  const sev = FLAG_SEVERITY[flag] || 'low';
  const colorMap = {
    high: { color: 'var(--c-danger)', bg: 'var(--c-danger-bg)' },
    medium: { color: 'var(--c-warning)', bg: 'var(--c-warning-bg)' },
    low: { color: 'var(--c-success)', bg: 'var(--c-success-bg)' },
  };
  const c = colorMap[sev];
  return (
    <span
      key={flag}
      style={{
        padding: '3px 8px',
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '.12em',
        color: c.color,
        background: c.bg,
        display: 'inline-block',
      }}
    >
      {flag}
    </span>
  );
};

const reviewBadge = (action) => {
  if (!action) return null;
  const isApproved = action === 'approved';
  return (
    <span style={{
      padding: '4px 12px',
      borderRadius: 'var(--r-pill)',
      fontFamily: 'var(--font-mono)',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '.08em',
      color: isApproved ? 'var(--c-success)' : 'var(--c-danger)',
      background: isApproved ? 'var(--c-success-bg)' : 'var(--c-danger-bg)',
    }}>
      {isApproved ? 'APPROVED' : 'REJECTED'}
    </span>
  );
};

const formatTs = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  return d.toLocaleString();
};

export default function FraudReviewQueue() {
  const [flagged, setFlagged] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({}); // id → true while write in-flight

  useEffect(() => {
    // Use the boolean hasFraudFlags field — `where('array', '!=', [])` is
    // unreliable in Firestore and may miss documents or require a full scan.
    // The fraud Cloud Function sets hasFraudFlags: true whenever flags are written.
    const q = query(
      collection(db, 'feedback_collection'),
      where('hasFraudFlags', '==', true),
      orderBy('capturedAt', 'desc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      // Already ordered by capturedAt desc; sort high-severity to the top within that.
      const items = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((d) => Array.isArray(d.fraudFlags) && d.fraudFlags.length > 0)
        .sort((a, b) => {
          const aHigh = a.fraudFlags.some((f) => FLAG_SEVERITY[f] === 'high');
          const bHigh = b.fraudFlags.some((f) => FLAG_SEVERITY[f] === 'high');
          return (bHigh ? 1 : 0) - (aHigh ? 1 : 0);
        });
      setFlagged(items);
      setLoading(false);
    }, (err) => {
      console.error('FraudReviewQueue snapshot error:', err);
      setLoading(false);
    });
    return unsub;
  }, []);

  const applyReview = async (item, action) => {
    setPending((p) => ({ ...p, [item.id]: true }));
    try {
      // 1. Mark the feedback submission itself as reviewed
      await updateDoc(doc(db, 'feedback_collection', item.id), {
        reviewedAt: Timestamp.now(),
        reviewAction: action,
      });

      // 2. Sync the igp_activity status so points are correctly awarded or withheld.
      //    'approved' → activity moves to approved (points counted).
      //    'rejected' → activity stays/moves to 'rejected' (points not counted).
      try {
        const actsSnap = await getDocs(
          query(
            collection(db, 'igp_activities'),
            where('referenceId', '==', item.id),
          )
        );
        for (const actDoc of actsSnap.docs) {
          await updateDoc(actDoc.ref, {
            status: action === 'approved' ? 'approved' : 'rejected',
            reviewedAt: Timestamp.now(),
          });
        }
      } catch (actErr) {
        // igp_activities update failure is non-fatal for the UI — log and continue
        console.error('FraudReviewQueue igp_activities sync error:', actErr);
      }
    } catch (err) {
      console.error('FraudReviewQueue updateDoc error:', err);
    } finally {
      setPending((p) => { const n = { ...p }; delete n[item.id]; return n; });
    }
  };

  return (
    <div className="db-page">
      <DepartureTopStrip />

      <div className="db-ribbon">
        <div className="db-ribbon-title">FRAUD REVIEW QUEUE</div>
        <div className="db-ribbon-actions">
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 11,
            letterSpacing: '.10em', color: 'var(--c-ink-3)',
          }}>
            {loading ? 'LOADING…' : `${flagged.length} FLAGGED`}
          </span>
        </div>
      </div>

      <section style={{ padding: 'var(--sp-6) var(--pad-h) var(--sp-12)' }}>
        {loading ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontFamily: 'var(--font-mono)', fontSize: 11,
            letterSpacing: '.12em', color: 'var(--c-ink-3)',
            padding: 'var(--sp-8) 0',
          }}>
            <div className="spinner" /> LOADING…
          </div>
        ) : flagged.length === 0 ? (
          <div style={{
            padding: 'var(--sp-12) var(--sp-6)', textAlign: 'center',
            background: 'var(--c-surface)', border: '1px solid var(--c-line)',
          }}>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 11,
              letterSpacing: '.16em', color: 'var(--c-success)',
              marginBottom: 'var(--sp-2)',
            }}>
              ALL CLEAR
            </div>
            <div style={{ fontFamily: "'Geist', sans-serif", fontSize: 13, color: 'var(--c-ink-3)' }}>
              No submissions flagged for review
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            {flagged.map((item) => (
              <div
                key={item.id}
                style={{
                  background: 'var(--c-surface)',
                  border: '1px solid var(--c-line)',
                  padding: 'var(--sp-4) var(--sp-5)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--sp-4)',
                }}
              >
                {/* Photo thumb */}
                <div style={{
                  width: 72, height: 72, flexShrink: 0,
                  background: 'var(--c-bg-2)', overflow: 'hidden',
                }}>
                  {item.photoUrl ? (
                    <img
                      src={item.photoUrl}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{
                      width: '100%', height: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--c-ink-4)',
                      letterSpacing: '.08em',
                    }}>
                      NO IMG
                    </div>
                  )}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Flag chips */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)', marginBottom: 'var(--sp-2)' }}>
                    {(item.fraudFlags || []).map((f) => flagChip(f))}
                  </div>

                  {/* ID + supervisor */}
                  <div style={{
                    fontFamily: 'var(--font-mono)', fontSize: 11,
                    letterSpacing: '.08em', color: 'var(--c-ink)',
                    fontVariantNumeric: 'tabular-nums slashed-zero',
                  }}>
                    {item.id.substring(0, 12).toUpperCase()}
                    {item.supervisorName ? (
                      <span style={{ marginLeft: 'var(--sp-3)', color: 'var(--c-ink-3)' }}>
                        · {item.supervisorName}
                      </span>
                    ) : null}
                  </div>

                  {/* Geo */}
                  {item.geoPoint && (
                    <div style={{
                      fontFamily: 'var(--font-mono)', fontSize: 10,
                      color: 'var(--c-ink-3)', marginTop: 2,
                      fontVariantNumeric: 'tabular-nums slashed-zero',
                    }}>
                      {item.geoPoint.latitude?.toFixed(5)}°N
                      {' · '}
                      {item.geoPoint.longitude?.toFixed(5)}°E
                      {item.geoAccuracy ? ` · ±${Math.round(item.geoAccuracy)}m` : ''}
                    </div>
                  )}

                  {/* Timestamp */}
                  {item.capturedAt && (
                    <div style={{
                      fontFamily: 'var(--font-mono)', fontSize: 10,
                      color: 'var(--c-ink-4)', marginTop: 2,
                      fontVariantNumeric: 'tabular-nums slashed-zero',
                    }}>
                      {formatTs(item.capturedAt)}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', flexShrink: 0, alignItems: 'flex-end' }}>
                  {item.reviewAction ? (
                    reviewBadge(item.reviewAction)
                  ) : (
                    <>
                      <button
                        disabled={!!pending[item.id]}
                        onClick={() => applyReview(item, 'approved')}
                        className="db-pill"
                        style={{
                          padding: '5px 14px',
                          fontSize: 10,
                          letterSpacing: '.10em',
                          background: 'var(--c-success)',
                          color: '#fff',
                          border: 'none',
                          cursor: pending[item.id] ? 'wait' : 'pointer',
                          opacity: pending[item.id] ? 0.6 : 1,
                        }}
                      >
                        APPROVE
                      </button>
                      <button
                        disabled={!!pending[item.id]}
                        onClick={() => applyReview(item, 'rejected')}
                        className="db-pill"
                        style={{
                          padding: '5px 14px',
                          fontSize: 10,
                          letterSpacing: '.10em',
                          background: 'transparent',
                          color: 'var(--c-danger)',
                          border: '1px solid var(--c-line)',
                          cursor: pending[item.id] ? 'wait' : 'pointer',
                          opacity: pending[item.id] ? 0.6 : 1,
                        }}
                      >
                        REJECT
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
